import React, { FC, useEffect, useState } from 'react';
import { Box, Typography, Grid, Button, TextField, Checkbox } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../../store';
import useStyles from './styles';
import {
  getAssetDeviceConfiguration,
  postAssetDeviceConfiguration,
} from '../../../store/configure/configuration/actions';

const Configuration: FC = () => {
  const classes = useStyles();
  const boxProperties = {
    p: 3,
    className: classes.box,
  };
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const configStatus = useSelector((state: AppState) => state.configStatus);
  const [enableMotionDetector, setEnableMotionDetector] = useState(true);
  const [motionDetection, setMotionDetection] = useState<number | null>();
  const [reportInterval, setReportInterval] = useState<number | null>();
  const [reportTime, setReportTime] = useState('');
  const [thresholdLow, setThresholdLow] = useState<number | null>();
  const [thresholdHigh, setThresholdHigh] = useState<number | null>();
  const validReportInterval =
    typeof reportInterval === 'number' && reportInterval >= 1 && reportInterval <= 1440;
  const validThresholdLow = typeof thresholdLow === 'number' && thresholdLow >= -128;
  const validThresholdHigh = typeof thresholdHigh === 'number' && thresholdHigh <= 127;
  const validReportTime = !reportTime || /^([0-2]?\d|1[0-2]):[0-5]\d$/.test(reportTime);
  const validMotionDetection =
    typeof motionDetection === 'number' && motionDetection >= 100 && motionDetection <= 16000;
  const disableSubmit =
    (enableMotionDetector === configStatus?.locator?.desiredSetting?.enableMotionDetect &&
      motionDetection === configStatus?.locator?.desiredSetting?.motionDetectionThreshold &&
      reportInterval === configStatus?.locator?.desiredSetting?.reportingInterval &&
      thresholdHigh === configStatus?.locator?.desiredSetting?.highTempThreshold &&
      thresholdLow === configStatus?.locator?.desiredSetting?.lowTempThreshold &&
      reportTime === configStatus?.endofDayReportTime?.desiredSetting?.endOfDay) ||
    !validMotionDetection ||
    !validReportTime ||
    !validThresholdHigh ||
    !validThresholdLow ||
    !validReportInterval;
  const validation = (check: boolean, message: string) => {
    if (!check) {
      return { error: !validReportInterval, helperText: message };
    }
    return {};
  };
  const { client } = useSelector((state: AppState) => state.login);
  const clientAuth: any = client?.authenticate();
  const userName = clientAuth.loginId;
  const dispatch = useDispatch();
  useEffect(() => {
    if (selectedEntity?.deviceId) {
      dispatch(
        getAssetDeviceConfiguration({
          make: selectedEntity.assetMake,
          serialNumber: selectedEntity.assetSerialNumber,
          deviceCharacterization: 'master',
          telematicsAssetId: selectedEntity.telematicsAssetId,
          deviceId: selectedEntity.deviceId,
          deviceType: selectedEntity.deviceType,
          deviceKey: selectedEntity.deviceKey,
        }),
      );
    }
  }, [selectedEntity]);
  useEffect(() => {
    setEnableMotionDetector(configStatus?.locator?.desiredSetting?.enableMotionDetect);
    setMotionDetection(configStatus?.locator?.desiredSetting?.motionDetectionThreshold);
    setReportInterval(configStatus?.locator?.desiredSetting?.reportingInterval);
    setThresholdHigh(configStatus?.locator?.desiredSetting?.highTempThreshold);
    setThresholdLow(configStatus?.locator?.desiredSetting?.lowTempThreshold);
    setReportTime(configStatus?.endofDayReportTime?.desiredSetting?.endOfDay);
  }, [configStatus]);
  const uploadConfig = () => {
    const locatorPayload: any = {
      enableMotionDetect:
        enableMotionDetector !== configStatus?.locator?.desiredSetting?.enableMotionDetect
          ? enableMotionDetector
          : undefined,
      motionDetectionThreshold:
        motionDetection !== configStatus?.locator?.desiredSetting?.motionDetectionThreshold
          ? motionDetection
          : undefined,
      reportingInterval:
        reportInterval !== configStatus?.locator?.desiredSetting?.reportingInterval
          ? reportInterval
          : undefined,
      lowTempThreshold:
        thresholdLow !== configStatus?.locator?.desiredSetting?.lowTempThreshold
          ? thresholdLow
          : undefined,
      highTempThreshold:
        thresholdHigh !== configStatus?.locator?.desiredSetting?.highTempThreshold
          ? thresholdHigh
          : undefined,
    };
    const endofDayReportTimePayload: any = {
      endOfDay:
        reportTime !== configStatus?.endofDayReportTime?.desiredSetting?.endOfDay
          ? reportTime
          : undefined,
    };
    dispatch(
      postAssetDeviceConfiguration({
        configs: [
          {
            deviceKey: selectedEntity?.deviceKey,
            endofDayReportTime: endofDayReportTimePayload,
            locator: locatorPayload,
          },
        ],
        wakeup: true,
        wakeupType: 'all',
        reboot: true,
        userId: userName,
        applicationId: 'iot-gateway',
      }),
    );
  };
  return (
    <Grid container direction="row" alignItems="stretch" className={classes.parentContainer}>
      <Grid item xs={12} md={6}>
        <Box {...boxProperties}>
          <div style={{ display: 'inline-flex', height: '1%' }}>
            <Typography variant="h6">
              Motion Detection Threshold
              <Typography variant="subtitle1" className={classes.descriptive}>
                (100 - 16000 mG)
              </Typography>
            </Typography>
            <Checkbox
              checked={enableMotionDetector}
              onChange={(event) => {
                setEnableMotionDetector(event.target.checked as boolean);
              }}
            />
          </div>
          <TextField
            variant="standard"
            className={classes.input}
            disabled={!enableMotionDetector}
            value={motionDetection}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event: any) => {
              const value = Number.parseInt(event.target.value, 10);
              setMotionDetection(Number.isNaN(value) ? null : value);
            }}
            {...validation(validMotionDetection, '100-16000')}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box {...boxProperties}>
          <Typography variant="h6">
            Reporting Interval
            <Typography variant="subtitle1" className={classes.descriptive}>
              (1-1440 min){' '}
            </Typography>
          </Typography>
          <TextField
            variant="standard"
            className={classes.input}
            value={reportInterval}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event: any) => {
              const value = Number.parseInt(event.target.value, 10);
              setReportInterval(Number.isNaN(value) ? null : value);
            }}
            {...validation(validReportInterval, '1-1440')}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box {...boxProperties}>
          <Typography variant="h6">
            Temperature Threshold Low
            <Typography variant="subtitle1" className={classes.descriptive}>
              (Min Value -128°C)
            </Typography>
          </Typography>
          <TextField
            variant="standard"
            className={classes.input}
            value={thresholdLow}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event: any) => {
              const value = Number.parseInt(event.target.value, 10);
              setThresholdLow(Number.isNaN(value) ? null : value);
            }}
            {...validation(validThresholdLow, '>= -128')}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box {...boxProperties}>
          <Typography variant="h6">
            Reporting Time
            <Typography variant="subtitle1" className={classes.descriptive}>
              (UTC Time in HH:mm format)
            </Typography>
          </Typography>
          <TextField
            variant="standard"
            className={classes.input}
            value={reportTime}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event) => setReportTime(event.target.value as string)}
            {...validation(validReportTime, 'XX:XX')}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box {...boxProperties}>
          <Typography variant="h6">
            Temperature Threshold High
            <Typography variant="subtitle1" className={classes.descriptive}>
              (Max Value 127°C)
            </Typography>
          </Typography>
          <TextField
            variant="standard"
            className={classes.input}
            value={thresholdHigh}
            FormHelperTextProps={{
              className: classes.helperText,
            }}
            onChange={(event: any) => {
              const value = Number.parseInt(event.target.value, 10);
              setThresholdHigh(Number.isNaN(value) ? null : value);
            }}
            {...validation(validThresholdHigh, '< 128')}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={12}>
        <Box className={classes.submitRow} p={3}>
          <Button
            variant="outlined"
            disabled={disableSubmit}
            onClick={() => {
              uploadConfig();
            }}
          >
            Add To Queue
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Configuration;
